
import { useContext, useEffect, useState } from 'react';
import { Button, Grid, Loader, Stack, NumberInput, Modal, Group, Textarea } from "@mantine/core";
import NotificationBanner from "../../../components/NotificationBanner/NotificationBanner";
import MantineDateInput from "../../../components/MantineDateInput/MantineDateInput";
import AuthContext from "../../../contexts/AuthContext";
import axios from "axios";
import showNotification from "../../../utils/showNotification";
import moment from "moment";
import './SalesInstallmentsForm.css'
import { IconCancel, IconCircleCheck } from "@tabler/icons-react";

const generateInstallmentItem = (form, index) => {
    const isPaid = form.values.installments.data[index].state === 'PAID';

    return (
        <Grid.Col span={12} key={index}>
            <div className="SalesPaymentForm-installments-grid-container">
                <Grid className="SalesPaymentForm-installments-grid" justify="flex-start" align="center">
                    <Grid.Col span={6}>
                        <NumberInput
                            withAsterisk
                            label={`Monto de cuota ${index + 1}`}
                            min={0}
                            step={0.1}
                            value={form.values.installments.data[index].agreedAmount}
                            onChange={(value) => {
                                form.setFieldValue(`installments.data.${index}.agreedAmount`, value);
                                form.setFieldValue(`installments.data.${index}.amount`, value);
                            }}
                            error={form.errors[`installments.data.${index}.agreedAmount`]}
                            disabled={isPaid}
                        />
                    </Grid.Col>
                    <Grid.Col span={5}>
                        <MantineDateInput
                            withAsterisk
                            label={`Fecha de cobro de cuota ${index + 1}`}
                            form={form}
                            name={`installments.data.${index}.due_date`}
                            disabled={isPaid}
                        />
                    </Grid.Col>
                    <Grid.Col span={1}>
                        {isPaid && <IconCircleCheck size={24} color="green" />}
                    </Grid.Col>
                </Grid>
            </div>
        </Grid.Col>
    );
};

function SalesInstallmentsForm(props) {
    const { form, loading, cancelPayment, isCancelling } = props;

    const { authToken } = useContext(AuthContext);
    const axiosConfig = {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`
        }
    };

    const getInstallmentsData = async () => {
        const paymentId = form.values.payment_id;
        const url = `${process.env.REACT_APP_BACKEND_SERVER}admin/administracion/installments/${paymentId}`;
        try {
            const { data } = await axios.get(url, axiosConfig);

            const installmentsData = data.installments.map(installment => ({
                due_date: moment(installment.date).toDate(),
                ...installment,
            }));

            form.setFieldValue("installments", {});
            form.setFieldValue("installments.data", installmentsData);
        } catch (error) {
            showNotification({
                color: "red",
                status: "error",
                title: "Operación fallida.",
                message: `No se pudo obtener la información de las cuotas restantes. Detalle: ${error.response ? error.response.data.message : error.message
                    }`,
                autoClose: 12000,
            });
        }
    };

    useEffect(() => {
        getInstallmentsData();
    }, []);

    // Recalcula las cuotas cuando cambia la cantidad deseada
    const handleInstallmentCountChange = (newCount) => {
        if (!newCount || newCount < 1) return;

        const current = form.values.installments.data ?? [];

        const paidInstallments = current.filter(i => i.state === 'PAID');
        const paidCount = paidInstallments.length;

        // No se puede tener menos cuotas que las ya pagadas
        if (newCount < paidCount) {
            showNotification({
                color: "yellow",
                status: "warning",
                title: "Cantidad inválida.",
                message: `No podés tener menos cuotas que las ya pagadas (${paidCount}).`,
                autoClose: 8000,
            });
            return;
        }

        const pendingInstallments = current.filter(i => i.state !== 'PAID');
        const pendingNeeded = newCount - paidCount;

        // Fecha base: la primera cuota PENDING existente, o hoy
        const baseDate = pendingInstallments.length > 0
            ? moment(pendingInstallments[0].due_date)
            : moment();

        // Construir el nuevo array de PENDING con la cantidad necesaria
        const newPendingInstallments = Array.from({ length: pendingNeeded }, (_, i) => {
            const existing = pendingInstallments[i];

            if (existing) {
                // Conservar la cuota pendiente existente tal como está
                return existing;
            }

            // Generar nueva cuota: fecha = baseDate + i meses
            return {
                due_date: moment(baseDate).add(i, 'months').toDate(),
                state: 'PENDING',
                agreedAmount: 0,
                cycle: i + 1,       // se renumeran abajo
                total_cycles: newCount,
                currency: 'USD',
                amount: 0,
            };
        });

        // Reconstruir el array completo: intercalar PAID en sus posiciones originales
        // Estrategia: insertar las PAID donde estaban (por _id o cycle), rellenar el resto con PENDING
        const finalInstallments = [];
        let pendingPointer = 0;

        // Primero, armamos un Set con los índices originales de las PAID
        const paidByOriginalIndex = {};
        current.forEach((inst, idx) => {
            if (inst.state === 'PAID') {
                paidByOriginalIndex[idx] = inst;
            }
        });

        // Si la cantidad nueva es menor que la original, las posiciones PAID
        // que caigan fuera del rango nuevo igual se incluyen al final
        const totalSlots = newCount;
        let paidQueue = [...paidInstallments]; // PAID en orden original
        let paidQueueIdx = 0;

        // Reconstruir: por cada slot, si en la posición original había una PAID, ponerla
        // De lo contrario, poner la siguiente PENDING
        for (let i = 0; i < totalSlots; i++) {
            const originalAtThisIndex = current[i];
            if (originalAtThisIndex && originalAtThisIndex.state === 'PAID') {
                // Posición original tenía una PAID → mantenerla
                finalInstallments.push({ ...originalAtThisIndex, cycle: i + 1, total_cycles: newCount });
                paidQueueIdx++;
            } else {
                // Poner la siguiente PENDING disponible
                if (pendingPointer < newPendingInstallments.length) {
                    finalInstallments.push({
                        ...newPendingInstallments[pendingPointer],
                        cycle: i + 1,
                        total_cycles: newCount,
                    });
                    pendingPointer++;
                }
            }
        }

        // Si quedaron PAID que estaban en posiciones mayores al newCount, agregarlas igual al final
        // (esto pasa si se intenta reducir por debajo de la última PAID)
        // → ya está cubierto por la validación de arriba (newCount >= paidCount)
        // pero por las dudas, si alguna PAID quedó afuera por estar en índice >= newCount:
        current.forEach((inst, idx) => {
            if (inst.state === 'PAID' && idx >= newCount) {
                finalInstallments.push({ ...inst, cycle: finalInstallments.length + 1, total_cycles: newCount });
            }
        });

        form.setFieldValue("installments.data", finalInstallments);
    };

    const [isConfirmModal, setIsConfirmModal] = useState(false)
    const [cancelReason, setCancelReason] = useState('')

    const closeConfirmModal = () => {
        setIsConfirmModal(false)
        setCancelReason('')
    }

    if (!form.values.installments?.data) return <Loader />;

    const installmentCount = form.values.installments.data.length;

    const lastPaidIndex = form.values.installments.data.reduce((max, inst, idx) => {
        return inst.state === 'PAID' ? idx + 1 : max;
    }, 1);


    return (
        <>
            <Grid
                style={{ width: "100%" }}
                className="SalesInstallmentsForm__grid__container"
                justify="center"
                align="center"
            >
                <NotificationBanner
                    type="information"
                    message={
                        `El importe de las cuotas fue calculado en base al valor del dólar de la fecha de la venta. ` +
                        `Considerar para cualquier ajuste posterior. ` +
                        `Referencia valor del dólar: $${form.values.dollar_day}`
                    }
                />

                {/* Control de cantidad de cuotas */}
                <Grid.Col span={12}>
                    <NumberInput
                        label="Cantidad de cuotas"
                        description="Al modificar este valor se agregan o eliminan cuotas pendientes. Las cuotas ya pagadas no se pueden quitar."
                        min={lastPaidIndex}
                        step={1}
                        value={installmentCount}
                        onChange={handleInstallmentCountChange}
                        style={{ maxWidth: 260 }}
                    />
                </Grid.Col>
                <Grid className="SalesInstallmentsForm__grid">
                    {form.values.installments.data.map((_, index) => (
                        generateInstallmentItem(form, index)
                    ))}
                </Grid>

                <Stack my={20}>
                    <Button color='red' m='auto' leftSection={<IconCancel />} type='button' onClick={() => setIsConfirmModal(true)}>
                        Cancelar todo el plan de pago
                    </Button>
                </Stack>


                <Grid.Col span={12}>
                    <Button
                        type="submit"
                        style={{ marginTop: "16px" }}
                        loading={loading}
                    >
                        Guardar cuotas
                    </Button>
                </Grid.Col>
            </Grid>
            <Modal
                opened={isConfirmModal}
                onClose={closeConfirmModal}
                title="Cancelar plan de pagos"
            >
                <Grid>
                    <Grid.Col span={12}>
                        <p className='ExamForm--modalText'>Estás a punto de cancelar un plan de pagos.</p>
                        <p className='ExamForm--modalText'>Esto va traer consecuencias tanto al usuario como a las comisiones de ventas</p>
                    </Grid.Col>

                    <Grid.Col span={12}>
                        <Textarea
                            withAsterisk
                            label="Motivo de la cancelación"
                            description="Queda registrado junto con tu usuario y la fecha."
                            placeholder="Ej: el alumno solicitó la baja del curso"
                            minRows={3}
                            autosize
                            maxLength={500}
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.currentTarget.value)}
                        />
                    </Grid.Col>

                    <Grid.Col span={12}>
                        <Group justify='center'>
                            <Button variant='outline' color='#DF3576'
                                onClick={closeConfirmModal}
                                disabled={isCancelling}
                            >
                                Cancelar
                            </Button>
                            <Button
                                type='button'
                                color='red'
                                loading={isCancelling}
                                disabled={cancelReason.trim().length < 5}
                                onClick={async (e) => {
                                    e.preventDefault()
                                    const ok = await cancelPayment({
                                        payment_id: form?.values?.payment_id,
                                        reason: cancelReason.trim(),
                                    })
                                    if (ok) closeConfirmModal()
                                }}
                            >
                                Confirmar
                            </Button>
                        </Group>
                    </Grid.Col>
                </Grid>
            </Modal>
        </>
    );
}

export default SalesInstallmentsForm;